import { Link } from "react-router-dom";
import { daDate } from "@/lib/date";
import { Archive, ExternalLink, CalendarClock, UserRound, MessageSquare, CheckCircle2, Unlock, PlusCircle } from "lucide-react";

export const SOURCE_LABEL = { audit_finding: "audit-fund", reklamation: "reklamation", le_meeting: "LE-møde", goal: "mål" };
const ST_BADGE = { "Åben": "bg-[#F1DEDA] text-[#A83232]", "I gang": "bg-[#F4EFEC] text-[#806B61]", "Lukket": "bg-[#E7EADF] text-[#5f6b4a]" };

export function sourceHref(src) {
  if (!src) return null;
  if (src.type === "audit_finding") return `/kvalitet/audit/fund/${src.id}`;
  if (src.type === "reklamation") return `/kvalitetsrapport/${src.id}`;
  if (src.type === "goal") return `/maal/${src.id}`;
  if (src.type === "le_meeting") return `/ledelse/moede/${src.id}`;
  return null;
}

export function SourceCard({ source: src, current, canAdmin }) {
  const href = sourceHref(src);
  const archived = current?.archived;
  return (
    <div className="rounded-2xl border border-[#EBE8E3] bg-white p-4" data-testid="le-action-source-card">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="label-caps">Oprindelse · {SOURCE_LABEL[src.type] || src.type}</p>
        {src.no && <span className="text-xs font-mono text-stone-400">{src.no}</span>}
        {archived && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0EDE8] text-stone-500 flex items-center gap-1" data-testid="le-action-source-archived"><Archive className="h-3 w-3" /> Arkiveret</span>}
        {current?.exists === false && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0EDE8] text-stone-500">Kilden findes ikke længere</span>}
        {current?.status && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F4EFEC] text-[#806B61]">Status nu: {current.status}</span>}
      </div>
      {src.title && <p className="text-sm font-medium text-[#1A1919] mt-1.5">{src.title}</p>}
      {src.problem_snapshot && <p className="text-sm text-stone-600 mt-1 whitespace-pre-wrap">{src.problem_snapshot}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-stone-500">
        {src.created_from === "proposal" && <span>Indstillet til LE af <span className="text-stone-700">{src.proposed_by_name || "—"}</span></span>}
        {current?.latest_review && <span data-testid="le-action-source-review">LE-effektvurdering: <span className={`px-1.5 py-0.5 rounded ${current.latest_review.verdict === "virker" ? "bg-[#E7EADF] text-[#5f6b4a]" : current.latest_review.verdict === "virker_ikke" ? "bg-[#F1DEDA] text-[#A83232]" : "bg-[#EFE7DC] text-[#8A6D3B]"}`}>{{ virker: "Virker", virker_ikke: "Virker ikke", foelg_op: "Følg op senere" }[current.latest_review.verdict]}</span> {daDate(current.latest_review.at)}</span>}
        {src.snapshot_at && <span>Øjebliksbillede {daDate(src.snapshot_at)}</span>}
        {href && (src.type !== "le_meeting" || canAdmin) && current?.exists !== false && <Link to={href} className="text-[#A83232] font-medium hover:underline flex items-center gap-1" data-testid="le-action-open-source">Åbn {SOURCE_LABEL[src.type]} <ExternalLink className="h-3 w-3" /></Link>}
      </div>
    </div>
  );
}

export function ConflictBanner({ message, testid = "conflict-banner" }) {
  if (!message) return null;
  return <div className="rounded-xl border border-[#E3D5C0] bg-[#FFF4E0] px-4 py-2.5 text-sm text-[#8A6D3B] flex items-center gap-2" data-testid={testid}><span className="font-semibold">⚠ Statuskonflikt:</span> {message}</div>;
}

export function LinkedActions({ data, excludeId, title = "Andre handlinger fra samme kilde" }) {
  const list = (data?.actions || []).filter((x) => x.id !== excludeId);
  return (
    <div data-testid="le-linked-actions">
      <div className="flex items-center justify-between mb-2"><p className="label-caps">{title}</p>{data?.summary && <span className="text-xs text-stone-500" data-testid="le-linked-summary">{data.summary}</span>}</div>
      <div className="space-y-1.5">
        {list.map((x) => (
          <Link key={x.id} to={`/handling/${x.id}`} data-testid={`le-linked-action-${x.id}`} className={`flex items-center gap-3 rounded-xl border border-[#EBE8E3] px-3 py-2 hover:border-stone-300 ${x.archived ? "opacity-60" : ""}`}>
            <span className="font-mono text-[11px] text-stone-400 shrink-0">{x.action_no}</span>
            <span className="text-sm text-stone-700 flex-1 truncate">{x.description}</span>
            {x.archived && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F0EDE8] text-stone-500">Arkiveret</span>}
            {x.conflict && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFF4E0] text-[#8A6D3B]" title={x.conflict}>⚠ Konflikt</span>}
            {x.deadline && <span className={`text-xs shrink-0 ${x.overdue ? "text-[#A83232]" : "text-stone-500"}`}>{daDate(x.deadline)}</span>}
            <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${ST_BADGE[x.status] || ""}`}>{x.status}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

const TL_ICON = { created: PlusCircle, closed: CheckCircle2, reopened: Unlock, deadline: CalendarClock, responsible: UserRound, note: MessageSquare };

export function buildTimeline(item) {
  const ev = [];
  if (item.created_at) ev.push({ kind: "created", at: item.created_at, by: item.created_by_name, text: "Handling oprettet" });
  (item.change_log || []).forEach((c, i) => ev.push({
    kind: c.field === "deadline" ? "deadline" : "responsible", at: c.at, by: c.by_name, key: `cl-${i}`,
    text: c.field === "deadline" ? `Frist ændret: ${c.from ? daDate(c.from) : "ingen"} → ${c.to ? daDate(c.to) : "ingen"}` : `Ansvarlig ændret: ${c.from || "—"} → ${c.to || "—"}`,
    sub: c.via === "followup" ? "via opfølgning" : c.via === "edit" ? "via redigering" : null,
  }));
  (item.followups || []).forEach((f) => {
    const isClose = f.status_to === "Lukket";
    const isReopen = f.status_from === "Lukket" && f.status_to && f.status_to !== "Lukket";
    ev.push({ kind: isClose ? "closed" : isReopen ? "reopened" : "note", at: f.at, by: f.by_name, key: f.id, meeting: f.meeting_title,
      text: isClose ? "Handling lukket" : isReopen ? "Handling genåbnet" : f.status_to ? `Status: ${f.status_from || "—"} → ${f.status_to}` : null, comment: f.comment });
  });
  return ev.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
}

export function Timeline({ item, events }) {
  const ev = events || buildTimeline(item);
  if (!ev.length) return <p className="text-sm text-stone-400">Ingen hændelser endnu.</p>;
  return (
    <ol className="relative border-l border-[#EBE8E3] ml-2 space-y-4" data-testid="le-timeline">
      {ev.map((e, i) => { const Icon = TL_ICON[e.kind] || MessageSquare; return (
        <li key={e.key || i} className="ml-5" data-testid={`le-timeline-${e.kind}`}>
          <span className={`absolute -left-[9px] h-4 w-4 rounded-full border-2 border-white flex items-center justify-center ${e.kind === "closed" ? "bg-[#5f6b4a]" : e.kind === "deadline" ? "bg-[#8A6D3B]" : "bg-[#806B61]"}`}><Icon className="h-2.5 w-2.5 text-white" /></span>
          {e.text && <p className="text-sm text-[#1A1919] font-medium">{e.text}{e.sub && <span className="text-xs text-stone-400 font-normal"> · {e.sub}</span>}</p>}
          {e.comment && <p className="text-sm text-stone-700 whitespace-pre-wrap">{e.comment}</p>}
          <p className="text-xs text-stone-400 mt-0.5">{e.by || "—"}{e.meeting ? ` · ${e.meeting}` : ""} · {daDate(e.at)}</p>
        </li>
      ); })}
    </ol>
  );
}
